import { useRef, useState } from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { BackButton } from '@/widgets/demo/ListPrimitives';
import { useAppNavigate, useBackToParent } from '@/hooks';
import { useToast } from '@/widgets/common';
import { importBatchesApi } from '@/services/importBatches/api';
import type { OcrDocType } from '@/services/importBatches/types';
import { isHandledAuthError } from '@/services/client';

type OcrImportState = 'idle' | 'uploading' | 'done';

const DOC_TYPE_OPTIONS: Array<{ value: OcrDocType; label: string; description: string }> = [
  { value: 'PROTECTION_LEDGER', label: '保護台帳', description: '受給者台帳のスキャンデータ（従来のOCR読取）' },
  { value: 'MOUSHIDESHO', label: '申出書', description: '申出書（別添様式4）のスキャンデータ。承認時に受給者と申出書を自動作成' },
];

function StepChip({
  index,
  label,
  active,
  completed,
}: {
  index: number;
  label: string;
  active: boolean;
  completed: boolean;
}) {
  return (
    <span className={`flex items-center space-x-1 ${active ? 'font-bold' : ''}`}>
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
          active || completed ? 'bg-orange-600 text-white' : 'bg-orange-200 text-orange-700'
        }`}
      >
        {index}
      </span>
      <span>{label}</span>
    </span>
  );
}

const OcrReadImport = () => {
  const navigate = useAppNavigate();
  const goBackToParent = useBackToParent();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importState, setImportState] = useState<OcrImportState>('idle');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [docType, setDocType] = useState<OcrDocType>('PROTECTION_LEDGER');
  const toast = useToast();

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const isPdfOrZip = (file: File) => {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.pdf') || lower.endsWith('.zip')) return true;
    const t = file.type;
    return t === 'application/pdf' || t === 'application/zip' || t === 'application/x-zip-compressed';
  };

  const completeUpload = async (file?: File | null) => {
    if (!file) return;
    if (!isPdfOrZip(file)) {
      toast.warning('PDF または ZIP のみアップロード可能です。');
      return;
    }
    setSelectedFileName(file.name);
    setImportState('uploading');
    try {
      await importBatchesApi.ocrUpload(file, docType);
      setImportState('done');
      toast.success('OCR 取込を受け付けました。処理状況は履歴で確認できます。');
    } catch (error) {
      setImportState('idle');
      if (isHandledAuthError(error)) return;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: 'データ取込', page: 'ocr-read-list' }, { label: 'ファイル取込' }]} />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-5">
          <h3 className="mb-2 text-sm font-bold text-orange-900">OCR読取の手順</h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-orange-800">
            <StepChip index={1} label="ファイル選択" active={importState === 'idle'} completed={importState !== 'idle'} />
            <svg className="h-4 w-4 shrink-0 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <StepChip index={2} label="アップロード中" active={importState === 'uploading'} completed={importState === 'done'} />
            <svg className="h-4 w-4 shrink-0 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <StepChip index={3} label="アップロード完了" active={importState === 'done'} completed={false} />
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,application/zip,.pdf,.zip"
          className="hidden"
          onChange={(event) => completeUpload(event.target.files?.[0] ?? null)}
        />

        {importState === 'idle' && (
          <div className="rounded-xl bg-white p-8 shadow-lg">
            <h2 className="mb-2 text-xl font-bold text-gray-800">
              {docType === 'MOUSHIDESHO' ? '申出書スキャンデータのアップロード' : '保護台帳スキャンデータのアップロード'}
            </h2>
            <p className="mb-4 text-sm text-gray-500">PDF（1 案件分）または ZIP（複数 PDF を一括）を指定し、OCR読取を実行します。</p>

            <div className="mb-6">
              <p className="mb-2 text-sm font-medium text-gray-700">読取する文書の種類</p>
              <div className="flex flex-wrap gap-3">
                {DOC_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-start gap-2 rounded-lg border px-4 py-3 transition ${
                      docType === opt.value
                        ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-400'
                        : 'border-gray-300 bg-white hover:border-orange-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="ocr-doc-type"
                      className="mt-1"
                      checked={docType === opt.value}
                      onChange={() => setDocType(opt.value)}
                    />
                    <span>
                      <span className="block text-sm font-bold text-gray-800">{opt.label}</span>
                      <span className="block text-xs text-gray-500">{opt.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                ※ 1 回のアップロードには同じ種類の文書のみ含めてください（保護台帳と申出書の混在不可）。
              </p>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                completeUpload(event.dataTransfer.files?.[0] ?? null);
              }}
              className="group cursor-pointer rounded-xl border-2 border-dashed border-gray-300 p-12 text-center transition-all hover:border-orange-400 hover:bg-orange-50/50"
            >
              <svg className="mx-auto mb-4 h-16 w-16 text-gray-400 transition-colors group-hover:text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-lg font-medium text-gray-700 transition-colors group-hover:text-orange-700">
                クリックまたはドラッグ＆ドロップでファイルを選択
              </p>
              <p className="mt-2 text-sm text-gray-500">対応フォーマット: PDF（単一案件）、ZIP（複数 PDF）</p>
            </div>
            <div className="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-800">
                注意: 個人情報を含むファイルの取り扱いにご注意ください。正規のスキャンデータのみアップロードしてください。
              </p>
            </div>
          </div>
        )}

        {importState === 'uploading' && (
          <div className="rounded-xl bg-white p-8 text-center shadow-lg">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
              <svg className="h-10 w-10 animate-spin text-orange-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h2 className="mb-3 text-2xl font-bold text-gray-800">アップロード中です…</h2>
            <p className="mx-auto max-w-lg text-gray-600">
              ファイル: <span className="font-medium text-gray-700">{selectedFileName}</span>
            </p>
          </div>
        )}

        {importState === 'done' && (
          <div className="rounded-xl bg-white p-8 text-center shadow-lg">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
              <CheckCircleOutlined className="text-4xl text-orange-600" />
            </div>
            <h2 className="mb-3 text-2xl font-bold text-gray-800">OCR読取用のファイルアップロードが完了しました。</h2>
            <p className="mx-auto mb-3 max-w-lg text-gray-600">
              アップロードされたファイルは OCR サービスで解析中です。処理状況はデータ取込から確認できます。
            </p>
            {selectedFileName ? (
              <p className="mb-8 text-sm text-gray-500">
                ファイル: <span className="font-medium text-gray-700">{selectedFileName}</span>
              </p>
            ) : null}
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('ocr-read-list')}
                className="cursor-pointer rounded-lg bg-orange-600 px-6 py-2 text-white shadow-md transition hover:bg-orange-700"
              >
                データ取込に戻る
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedFileName('');
                  setImportState('idle');
                }}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 transition hover:bg-gray-50"
              >
                続けて読取
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default OcrReadImport;
